/**
 * **The booth station** (`Booth.dc.html`, operator screen 1440 — the seventh overnight): what an
 * open check-in station shows. One search box that owns the focus; a name, an old name or a
 * phone finds a volunteer with their shift on the booth's day; the one card says who, today and
 * the tag, then **Check in** (Enter) or **Check out**; **New person** is the walk-up — a name and
 * a phone, placed on the shift that is shortest. The day's count is always on the right.
 *
 * The operator's other hands (7b): **Someone else is working it** and **Cover for someone** (the
 * signup stays, the person changes), **Reassign** (one verb), **Undo** with a name and a reason,
 * **Print the tag again**, the wrong day — somebody on another day put on a shift today with a
 * name and a reason — and **See the booth chair** in place of Check in for a flagged person.
 * Issued at the booth (7e): a chair's radio or key on the key log, back at check-out.
 * **A group** (ON10 item 4): the leader's QR opens the group card — tap who is here, Check in N,
 * and one batch of tags prints with the group's name.
 * **The offline day** (7g): with no network the desk works from the day kept here — find, check
 * in and out, undo, reassign, scan and print queue with their real times, the one sentence says
 * how many wait, and the queue seals in order when the network is back.
 *
 * **Who's at the desk** (7f): the operator finds their name once and every action takes it;
 * "I'm taking over" hands the desk on. **Standby**: here and willing with no shift — the booth
 * sends them where it is short ("send Dan to Parking") or releases them.
 *
 * Drawn and not here yet, so not shown (a button with nothing behind it is never drawn): the
 * operator's day PIN and passkey at the station, "Still Linda?" after ten idle minutes, groups,
 * the household card, the label printer —
 * the tag prints through the browser's own print. A station that is not on a festival day works
 * the first festival day and says so on every screen.
 *
 * Held to its chrome until the operator screen is scored whole against the drawing.
 */
import { type ReactNode } from 'react';
import { formatTime } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { queueSays } from './boothOffline.js';
import { AdminStation, ShelterRoll, WhosHereNow } from './BoothAdmin.js';
import { DeskSignIn } from './BoothDesk.js';
import { CloseTheBooth, OpenTheBooth } from './BoothOpenClose.js';
import { BoothLists, BoothTomorrow, EnterThePaper } from './BoothPaper.js';
import { TagCard, TagPreview } from './BoothScan.js';
import { first, hours } from './BoothShared.js';
import { BoothProvider, useBooth } from './BoothState.js';
import { BoothCheckIn } from './BoothCheckIn.js';
import { BoothCount, GuestCard } from './BoothCount.js';
import './booth.css';

export function BoothStation({
  onClose,
  label,
}: {
  onClose: () => void;
  /** The station's own name, from its session — the bar says it even with no day to open. */
  label?: string | undefined;
}): ReactNode {
  return (
    <BoothProvider label={label}>
      <StationFrame onClose={onClose} label={label} />
    </BoothProvider>
  );
}

/** The station's bar, its heading and the admin panels; check-in and the count are their own parts. */
function StationFrame({
  onClose,
  label,
}: {
  onClose: () => void;
  label: string | undefined;
}): ReactNode {
  const {
    today,
    synced,
    trouble,
    picked,
    walkUp,
    walkUpFrom,
    printerOut,
    setPrinterOut,
    left,
    reprinted,
    already,
    covering,
    setDone,
    tag,
    setTag,
    batch,
    group,
    house,
    busy,
    panel,
    setPanel,
    guest,
    queue,
    offlineSince,
    offline,
    desk,
    deskOpen,
    setDeskOpen,
    standby,
    sendTo,
    setSendTo,
    hand,
    loadDesk,
    load,
    lastTouch,
    stillAsk,
    setStillAsk,
    act,
    day,
    shortest,
    placeChoices,
    expectedShift,
    inShift,
    undoInAt,
    isChair,
    tablet,
    usesTags,
    printTag,
    signingIn,
    stationName,
    title,
  } = useBooth();

  return (
    <div className="booth" data-screen="booth" data-booth="station">
      <header className="booth__bar">
        <span className="booth__brand">CIDER · CHECK-IN</span>
        <span className="booth__station">
          {tablet ? '' : 'THE BOOTH · '}
          {(today?.label ?? label ?? '').toUpperCase()}
        </span>
        <span className="booth__operator" data-testid="booth-desk">
          {desk ? `${desk.name} at the desk` : 'No one signed in'}
        </span>
        <span className="booth__health">
          <span className="booth__light">
            <span className={`booth__dot booth__dot--${offline ? 'warn' : 'ok'}`} />
            {offline
              ? `Last sync ${synced ? formatTime(synced) : '—'}`
              : synced
                ? `Synced ${formatTime(synced)}`
                : 'Syncing'}
          </span>
          <span className="booth__light" data-testid="booth-printer">
            <span
              className={`booth__dot booth__dot--${tablet ? 'warn' : printerOut ? 'bad' : 'ok'}`}
            />
            {tablet
              ? 'No printer · tags at the booth'
              : printerOut
                ? 'Tag printer out'
                : 'Tags print here'}
          </span>
          <span className="booth__light">
            <span className={`booth__dot booth__dot--${guest?.on ? 'ok' : 'warn'}`} />
            {guest?.on ? 'Guest screen on' : 'Guest screen off'}
          </span>
        </span>
        <details className="booth__menu">
          <summary className="booth__close">Station</summary>
          <div
            className="booth__menulist"
            onClick={(e) => {
              // A pick closes the menu; the station's screen is what it opens.
              const menu = e.currentTarget.parentElement;
              if (menu instanceof HTMLDetailsElement) menu.open = false;
            }}
          >
            <button
              type="button"
              className="booth__close"
              onClick={() => {
                setDeskOpen(true);
              }}
            >
              {desk ? 'I’m taking over' : 'Who’s at the desk?'}
            </button>
            {tablet ? null : (
              <button
                type="button"
                className="booth__close"
                onClick={() => {
                  // A tag already printed (or not) is not this switch's to report again.
                  setTag(null);
                  setDone(null);
                  setPrinterOut(!printerOut);
                }}
              >
                {printerOut ? 'The tag printer is back' : 'The tag printer is out'}
              </button>
            )}
            {desk ? (
              <button
                type="button"
                className="booth__close"
                onClick={() => {
                  api<{ says: string }>('POST', '/volunteers/booth/desk/sign-out')
                    .then((r) => {
                      setDone(r.says);
                      loadDesk();
                    })
                    .catch((e: unknown) => {
                      setDone(sentence(e));
                    });
                }}
              >
                Sign out of the desk
              </button>
            ) : null}
            <button
              type="button"
              className="booth__close"
              onClick={() => {
                setPanel(panel === 'open' ? null : 'open');
              }}
            >
              Open the booth
            </button>
            <button
              type="button"
              className="booth__close"
              onClick={() => {
                setPanel(panel === 'close' ? null : 'close');
              }}
            >
              Close the booth
            </button>
            <button
              type="button"
              className="booth__close"
              onClick={() => {
                setPanel(panel === 'paper' ? null : 'paper');
              }}
            >
              Enter the paper
            </button>
            <button
              type="button"
              className="booth__close"
              onClick={() => {
                setPanel(panel === 'admin' ? null : 'admin');
              }}
            >
              Admin station
            </button>
            <button type="button" className="booth__close" onClick={onClose}>
              Close this station
            </button>
          </div>
        </details>
      </header>
      <div className="booth__body">
        <main className="booth__main">
          <div className="booth__head">
            <div className="booth__eyebrow" data-testid="booth-day">
              {signingIn
                ? `${stationName.toUpperCase()} · ${formatTime(new Date())} · ${deskOpen && desk ? 'HANDOFF' : 'BEFORE THE FIRST CHECK-IN'}`
                : panel === 'tag'
                  ? 'PRINT → CHECK-IN TAG'
                  : panel
                    ? `ADMIN · ${formatTime(new Date())}`
                    : left && !picked
                      ? `SCANNED · ${first(left.name).toUpperCase()}’S TAG · ${formatTime(left.outAt)}`
                      : reprinted && !picked
                        ? `${reprinted.name.toUpperCase()} · LOST THEIR TAG`
                        : picked && offline
                          ? `FOUND ON THE CACHED ROSTER · ${formatTime(new Date())}`
                          : picked && !hand && picked.today.length === 0 && picked.otherDays.length
                            ? 'FOUND · NOT ON TODAY'
                            : picked && !hand && isChair
                              ? `FOUND · ${(picked.chairOf?.[0] ?? 'A').toUpperCase()} CHAIR`
                              : picked && already?.personId === picked.personId
                                ? `SCANNED · ${first(picked.name).toUpperCase()}’S TAG · ${formatTime(new Date())}`
                                : picked && !hand && inShift?.inAt && !expectedShift
                                  ? `CHECKED IN ${formatTime(inShift.inAt)}`
                                  : picked && hand?.kind === 'undo'
                                    ? `${picked.name.toUpperCase()}${undoInAt}`
                                    : picked && hand?.kind === 'reassign'
                                      ? `ONE VERB · THE SAME RULES WHEREVER IT’S PRESSED · ${formatTime(new Date())}`
                                      : picked && hand?.kind === 'swap'
                                        ? `SIGNED UP: ${picked.name.toUpperCase()} · WHO IS STANDING HERE?`
                                        : picked && hand?.kind === 'cover'
                                          ? `“I’M HERE FOR ${covering ? first(covering).toUpperCase() : '…'}”`
                                          : walkUp
                                            ? `NO MATCH FOR “${walkUpFrom.toUpperCase()}” · WALK-UP`
                                            : house
                                              ? `SCANNED · THE FAMILY PAGE’S QR · ${formatTime(house.at)}`
                                              : group
                                                ? `SCANNED · THE LEADER’S QR · ${formatTime(group.at)}`
                                                : today
                                                  ? `${today.label.toUpperCase()} · ${day}`
                                                  : 'THE BOOTH'}
              {today?.lookOnly ? ' · A LOOK' : today?.practice ? ' · PRACTICE DAY' : ''}
              {today?.demo ? ' · DEMO YEAR' : ''}
            </div>
            <h1 className="booth__title">{title}</h1>
          </div>
          {offline || queue.length > 0 ? (
            <p className="booth__queue" role="status" data-testid="booth-queue">
              {queueSays(queue.length, offlineSince, formatTime)}
              {offline ? ' — the desk works from today’s roster kept here.' : ''}
            </p>
          ) : null}
          {today?.lookOnly ? (
            <p
              className="booth__practice booth__practice--look"
              role="alert"
              data-testid="booth-look"
            >
              {today.lookSays}
            </p>
          ) : today?.practice ? (
            <p className="booth__practice" data-testid="booth-practice">
              PRACTICE DAY{today.demo ? ' · DEMO YEAR' : ''} — today is not a festival day, so the
              booth is open for {day}, the first one. Check-ins made here are kept in{' '}
              {String(today.festivalYear)}’s record.
            </p>
          ) : null}
          {trouble ? (
            <p className="booth__refusal" role="alert">
              {trouble}
            </p>
          ) : null}

          {stillAsk && desk ? (
            <section
              className="booth__card"
              role="dialog"
              aria-label="Still at the desk?"
              data-testid="booth-still"
            >
              <div className="booth__cardhead">STILL {first(desk.name).toUpperCase()}?</div>
              <p className="booth__fine">Nothing has been touched for ten minutes.</p>
              <button
                type="button"
                className="booth__big booth__big--navy"
                onClick={() => {
                  lastTouch.current = Date.now();
                  setStillAsk(false);
                }}
              >
                Yes, still me
              </button>
              <button
                type="button"
                className="booth__link"
                onClick={() => {
                  setStillAsk(false);
                  void api('POST', '/volunteers/booth/desk/sign-out')
                    .catch(() => undefined)
                    .finally(() => {
                      loadDesk();
                      setDeskOpen(true);
                    });
                }}
              >
                Someone else is at the desk
              </button>
            </section>
          ) : null}
          {signingIn ? (
            <DeskSignIn
              taking={deskOpen ? (desk?.name ?? null) : null}
              station={stationName}
              onSignedIn={(says) => {
                setDone(says);
                setDeskOpen(false);
                loadDesk();
              }}
              onCancel={
                deskOpen
                  ? () => {
                      setDeskOpen(false);
                    }
                  : null
              }
            />
          ) : null}
          {panel === 'open' ? (
            <OpenTheBooth
              synced={synced}
              day={day}
              onTestTag={() => {
                setTag({ name: 'Test tag', area: 'The booth', hours: '', day });
                setTimeout(() => {
                  printTag();
                }, 50);
              }}
              onPreview={() => {
                setPanel('tag');
              }}
              printer={!tablet}
            />
          ) : null}
          {panel === 'tag' ? (
            <TagPreview
              year={today?.festivalYear ?? null}
              tags={(today?.lastIn ?? []).slice(0, 3).map((l) => ({
                name: l.name,
                area: l.area,
                hours: hours(l.startsAt, l.endsAt),
                day,
              }))}
            />
          ) : null}
          {panel === 'paper' ? <EnterThePaper desk={desk?.name ?? null} /> : null}
          {panel === 'admin' ? <AdminStation onOpen={setPanel} /> : null}
          {panel === 'tomorrow' ? <BoothTomorrow /> : null}
          {panel === 'whoshere' ? (
            <WhosHereNow
              onShelter={() => {
                setPanel('shelter');
              }}
            />
          ) : null}
          {panel === 'shelter' ? <ShelterRoll /> : null}
          {panel === 'lists' ? (
            <BoothLists>
              <section className="booth__card" data-testid="booth-standby-card">
                <div className="booth__cardhead">STANDBY · HERE, CHECKED IN, WILLING</div>
                <ul className="booth__list" data-testid="booth-standby">
                  {standby.length === 0 ? (
                    <li className="booth__fine">Nobody on standby.</li>
                  ) : (
                    standby.map((sb) => (
                      <li key={sb.id} className="booth__standby">
                        <span>
                          <strong>{sb.name}</strong>
                          <br />
                          since {formatTime(sb.since)}
                          {sb.note ? ` · ${sb.note}` : ''}
                        </span>
                        <select
                          className="booth__input"
                          aria-label={`Send ${sb.name} to`}
                          value={sendTo[sb.id] ?? shortest[0]?.shiftId ?? ''}
                          onChange={(e) => {
                            setSendTo({ ...sendTo, [sb.id]: e.target.value });
                          }}
                        >
                          {placeChoices.map((x) => (
                            <option key={x.shiftId} value={x.shiftId}>
                              {x.area} · {hours(x.startsAt, x.endsAt)}
                              {x.short > 0 ? ` · short ${String(x.short)}` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="booth__btn"
                          disabled={busy}
                          onClick={() => {
                            const to = sendTo[sb.id] ?? shortest[0]?.shiftId ?? '';
                            act(`/volunteers/booth/standby/${sb.id}/send`, { shiftId: to }, (r) => {
                              const x = r as {
                                says: string;
                                tag?: string;
                                area?: string;
                                startsAt?: string;
                                endsAt?: string;
                                name?: string;
                              };
                              setDone(x.says);
                              if (x.area && x.startsAt && x.endsAt)
                                setTag({
                                  name: x.name ?? sb.name,
                                  area: x.area,
                                  hours: hours(x.startsAt, x.endsAt),
                                  day,
                                  code: x.tag,
                                });
                            });
                          }}
                        >
                          Send
                        </button>
                        <button
                          type="button"
                          className="booth__link"
                          onClick={() => {
                            act(`/volunteers/booth/standby/${sb.id}/release`, {}, (r) => {
                              setDone(`${sb.name}: ${r.says}`);
                            });
                          }}
                        >
                          Release
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </section>
            </BoothLists>
          ) : null}
          {panel === 'close' ? <CloseTheBooth onClosed={load} queued={queue.length} /> : null}
          <BoothCheckIn />
        </main>

        <aside className="booth__side">
          <GuestCard />
          <BoothCount />
        </aside>
      </div>
      {(batch ?? (tag ? [tag] : []))
        .filter((t) => usesTags(t.area))
        .map((t) => (
          <TagCard key={`${t.name}${t.code ?? ''}`} tag={t} />
        ))}
    </div>
  );
}
